import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import SchemaOrg from "@/components/SchemaOrg";
import AnalyticsLoader from "@/components/AnalyticsLoader";
import FeedbackButton from "@/components/FeedbackButton";
import FreeAccessModal from "@/components/FreeAccessModal";
import UpgradeMoment from "@/components/UpgradeMoment";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";

// Route-level code splitting for non-critical routes (CWV / TTI optimization).
const ReadingPractice = lazy(() => import("@/pages/ReadingPractice"));
const PassageReader = lazy(() => import("@/pages/PassageReader"));
const Vocabulary = lazy(() => import("@/pages/Vocabulary"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Companion = lazy(() => import("@/pages/Companion"));
const TestLab = lazy(() => import("@/pages/TestLab"));
const SATPrep = lazy(() => import("@/pages/SATPrep"));
const FluencyStudio = lazy(() => import("@/pages/FluencyStudio"));
const AnnotatorAI = lazy(() => import("@/pages/AnnotatorAI"));
const Insights = lazy(() => import("@/pages/Insights"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const VocabularyDuel = lazy(() => import("@/pages/VocabularyDuel"));
const TeacherDashboard = lazy(() => import("@/pages/TeacherDashboard"));
const ParentDashboard = lazy(() => import("@/pages/ParentDashboard"));
const ReadingTopicsIndex = lazy(() => import("@/pages/ReadingTopicsIndex"));
const LandingTopic = lazy(() => import("@/pages/LandingTopic"));
const Faq = lazy(() => import("@/pages/Faq"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const AdminSeo = lazy(() => import("@/pages/AdminSeo"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Accessibility = lazy(() => import("@/pages/Accessibility"));
const DailySpark = lazy(() => import("@/pages/DailySpark"));

import "@/App.css";

function RouteFallback() {
  return (
    <div className="min-h-[60vh] grid place-items-center text-sm text-muted-foreground" data-testid="route-loading">
      Loading…
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <SchemaOrg />
            <AnalyticsLoader />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/register" element={<Register />} />
                <Route path="/practice" element={<ReadingPractice />} />
                <Route path="/practice/:id" element={<PassageReader />} />
                <Route path="/vocabulary" element={<Vocabulary />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/companion" element={<Companion />} />
                <Route path="/test-lab" element={<TestLab />} />
                <Route path="/sat-prep" element={<SATPrep />} />
                <Route path="/fluency" element={<FluencyStudio />} />
                <Route path="/annotator" element={<AnnotatorAI />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
                <Route path="/duel" element={<VocabularyDuel />} />
                <Route path="/teacher" element={<TeacherDashboard />} />
                <Route path="/parent" element={<ParentDashboard />} />
                {/* SEO landing pages */}
                <Route path="/reading" element={<ReadingTopicsIndex />} />
                <Route path="/reading/:slug" element={<LandingTopic />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/admin/seo" element={<AdminSeo />} />
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/accessibility" element={<Accessibility />} />
                <Route path="/daily-spark" element={<DailySpark />} />
              </Routes>
            </Suspense>
            <FeedbackButton />
            <FreeAccessModal />
            <UpgradeMoment />
            <Toaster position="top-right" richColors closeButton />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
